import React, { useState } from 'react';
import { Clock, DollarSign, Calendar, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import supplierBillingService from '../../../../services/supplierBillingService';
import { toast } from 'react-toastify';

const SupplierBillingSection = ({ ticket, onBillingUpdate }) => {
    const [showHoursForm, setShowHoursForm] = useState(false);
    const [hoursData, setHoursData] = useState({
        hours_worked: '',
        work_description: '',
        work_date: new Date().toISOString().split('T')[0]
    });
    const [submitting, setSubmitting] = useState(false);

    // Check if billing is enabled for this ticket
    const isBillingEnabled = ticket?.billing_enabled == 1;
    const billingHours = ticket?.billing_hours || [];
    const totalHours = supplierBillingService.calculateTotalHours(billingHours);
    const hourlyRate = parseFloat(ticket?.hourly_rate || 0);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!hoursData.hours_worked || parseFloat(hoursData.hours_worked) <= 0) {
            toast.error('Please enter valid hours worked');
            return;
        }

        if (!hoursData.work_description.trim()) {
            toast.error('Please provide a description of work done');
            return;
        }

        if (!hoursData.work_date) {
            toast.error('Please select a work date');
            return;
        }

        setSubmitting(true);
        try {
            await supplierBillingService.submitBillingHours(ticket.ticket_id, hoursData);
            toast.success('Billing hours submitted successfully');
            
            // Reset form
            setHoursData({
                hours_worked: '',
                work_description: '',
                work_date: new Date().toISOString().split('T')[0]
            });
            setShowHoursForm(false);

            // Notify parent to reload ticket
            if (onBillingUpdate) {
                onBillingUpdate();
            }
        } catch (error) {
            console.error('Error submitting hours:', error);
            toast.error(error.response?.data?.message || 'Failed to submit billing hours');
        } finally {
            setSubmitting(false);
        }
    };

    const getStatusIcon = (status) => {
        switch(status?.toLowerCase()) {
            case 'approved':
                return <CheckCircle className="w-4 h-4 text-green-600" />;
            case 'rejected':
                return <XCircle className="w-4 h-4 text-red-600" />;
            case 'pending':
            default:
                return <AlertCircle className="w-4 h-4 text-yellow-600" />;
        }
    };

    // Don't show billing section if billing is not enabled
    if (!isBillingEnabled) {
        return null;
    }

    return (
        <div className="bg-white shadow rounded-lg p-6">
            <div className="flex justify-between items-start mb-6">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                        <DollarSign className="w-5 h-5 mr-2 text-blue-600" />
                        Billing Information
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                        Track your billable hours for this ticket
                    </p>
                </div>
                {!showHoursForm && (
                    <button
                        onClick={() => setShowHoursForm(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center text-sm"
                    >
                        <Clock className="w-4 h-4 mr-2" />
                        Submit Hours
                    </button>
                )}
            </div>

            {/* Billing Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-blue-900">Total Hours</p>
                            <p className="text-2xl font-bold text-blue-700 mt-1">
                                {supplierBillingService.formatHours(totalHours)}
                            </p>
                        </div>
                        <Clock className="w-8 h-8 text-blue-600 opacity-50" />
                    </div>
                </div>

                {hourlyRate > 0 && (
                    <>
                        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-green-900">Hourly Rate</p>
                                    <p className="text-2xl font-bold text-green-700 mt-1">
                                        R{hourlyRate.toFixed(2)}
                                    </p>
                                </div>
                                <DollarSign className="w-8 h-8 text-green-600 opacity-50" />
                            </div>
                        </div>

                        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-purple-900">Estimated Total</p>
                                    <p className="text-2xl font-bold text-purple-700 mt-1">
                                        R{(totalHours * hourlyRate).toFixed(2)}
                                    </p>
                                </div>
                                <DollarSign className="w-8 h-8 text-purple-600 opacity-50" />
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Submit Hours Form */}
            {showHoursForm && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-md font-semibold text-gray-900">Submit Billable Hours</h3>
                        <button
                            onClick={() => setShowHoursForm(false)}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            <XCircle className="w-5 h-5" />
                        </button>
                    </div>
                    
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    <Clock className="w-4 h-4 inline mr-1" />
                                    Hours Worked *
                                </label>
                                <input
                                    type="number"
                                    step="0.5"
                                    min="0.5"
                                    max="24"
                                    value={hoursData.hours_worked}
                                    onChange={(e) => setHoursData({...hoursData, hours_worked: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="e.g., 4.5"
                                    required
                                />
                                <p className="text-xs text-gray-500 mt-1">Enter hours in decimal format (e.g., 2.5 for 2.5 hours)</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    <Calendar className="w-4 h-4 inline mr-1" />
                                    Work Date *
                                </label>
                                <input
                                    type="date"
                                    value={hoursData.work_date}
                                    onChange={(e) => setHoursData({...hoursData, work_date: e.target.value})}
                                    max={new Date().toISOString().split('T')[0]}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Work Description *
                            </label>
                            <textarea
                                value={hoursData.work_description}
                                onChange={(e) => setHoursData({...hoursData, work_description: e.target.value})}
                                rows="3"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Describe the work performed during this time..."
                                required
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Provide details about the work performed for accurate billing
                            </p>
                        </div>

                        <div className="flex justify-end space-x-3 pt-2">
                            <button
                                type="button"
                                onClick={() => setShowHoursForm(false)}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                                disabled={submitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="w-4 h-4 mr-2" />
                                        Submit Hours
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Billing Hours History */}
            <div>
                <h3 className="text-md font-semibold text-gray-900 mb-3">Hours History</h3>
                
                {billingHours.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                        <Clock className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                        <p>No billing hours submitted yet</p>
                        <p className="text-sm">Click "Submit Hours" to add your first entry</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {billingHours.map((entry, index) => (
                            <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-1">
                                            <span className="font-semibold text-gray-900">
                                                {supplierBillingService.formatHours(entry.hours_worked)} hours
                                            </span>
                                            <span className="text-gray-500">•</span>
                                            <span className="text-sm text-gray-600">
                                                {supplierBillingService.formatDate(entry.work_date)}
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-700 mb-2">
                                            {entry.work_description}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            Submitted: {supplierBillingService.formatDateTime(entry.submitted_at)}
                                        </p>
                                    </div>
                                    <div className="ml-4">
                                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${supplierBillingService.getStatusBadgeClass(entry.status)}`}>
                                            {getStatusIcon(entry.status)}
                                            <span className="ml-1 capitalize">{entry.status || 'pending'}</span>
                                        </span>
                                    </div>
                                </div>

                                {/* Show review notes if available */}
                                {entry.review_notes && (
                                    <div className="mt-3 pt-3 border-t border-gray-200">
                                        <p className="text-xs font-medium text-gray-700 mb-1">Review Notes:</p>
                                        <p className="text-xs text-gray-600 bg-white rounded p-2">
                                            {entry.review_notes}
                                        </p>
                                        {entry.reviewed_at && (
                                            <p className="text-xs text-gray-500 mt-1">
                                                Reviewed: {supplierBillingService.formatDateTime(entry.reviewed_at)}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Billing Notes from IT */}
            {ticket.billing_notes && (
                <div className="mt-6 bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h4 className="text-sm font-semibold text-blue-900 mb-2">Billing Instructions:</h4>
                    <p className="text-sm text-blue-800">{ticket.billing_notes}</p>
                </div>
            )}
        </div>
    );
};

export default SupplierBillingSection;
